import { useEffect } from 'react'

import { getConversation } from 'conversations/modules/conversation/selectors'
import { getOrgChannel, getSocketSetupComplete } from 'conversations/modules/socket/selectors'
import { getCurrentUserId } from 'conversations/modules/user/selectors'
import { updateBadgeCounts } from 'conversations/utils/badgeUtils'
import {
  isConversationInCurrentInbox,
  isConversationInSomeSection,
  isCurrentConversationId,
  updateCounts,
} from 'conversations/utils/sectionUtils'
import { batch, useDispatch, useSelector } from 'react-redux'
import { Action } from 'redux'
import { DesktopPushNotificationPayload } from 'shared/notifications/DesktopPushNotification'
import { receiveConversationUpdateFromWebsocket } from 'shared/store/actions/conversation'
import { receiveGatesUpdate } from 'shared/store/actions/gates'
import { receiveNotification } from 'shared/store/actions/notifications'
import { track } from 'shared/store/actions/tracking'
import { receiveUser, trafficControlUpdateUserConfig } from 'shared/store/actions/user'
import ConversationType from 'shared/typings/conversations/Conversation'
import { ConversationStatus } from 'shared/typings/conversations/status/ConversationStatus'
import { MessageRecipientStatus } from 'shared/typings/messages/MessageRecipientStatus'
import { Gates, User } from 'shared/typings/user/User'
import { getUserIdTypeNumber } from 'shared/typings/UserIdType'

import { UserTypeIF } from '@driftt/app-config/dist/utils/driftTypes'
import { CLUSTER, getDriftWebSocket } from '@driftt/drift-ws'
import { access, GlobalStore as store } from '@driftt/traffic-control'

import { RootState } from 'typesafe-actions'

export interface MessageRecipientStatusWS extends Omit<MessageRecipientStatus, 'recipientId'> {
  recipientId: number
}

interface Props {
  orgId?: number
  currentUserId?: number
  currentUserType?: UserTypeIF
}

const receiveNoLongerParticipant = (conversation: ConversationType): boolean => {
  const currentUserId = getCurrentUserId(store.getState()) ?? 0
  const storedConversation = getConversation(conversation.id)(store.getState())
  const wasParticipant = storedConversation?.participantIds.includes(currentUserId)
  const noLongerParticipant = !conversation.participantIds.includes(currentUserId)

  return !!(wasParticipant && noLongerParticipant)
}

const hasPermissionToSeeConversation = (conversation: ConversationType): boolean => {
  const currentUserId = getCurrentUserId(store.getState()) ?? 0

  if (!access.isRbacUser()) {
    const hasNoParticipants = access.hasScopes('convo-WITH_NO_PARTICIPANTS')

    return (
      access.isAdmin() ||
      access.hasScopes('convo-ALL') ||
      conversation.participantIds.includes(currentUserId) ||
      (hasNoParticipants && !conversation.participantIds.length)
    )
  }

  if (access.hasPermissionForWorkspace('convo_view_all', 'READ', 'global')) {
    return true
  }

  if (
    access.hasFeatures([{ featureName: 'convo_view_no_participants', accessLevel: 'READ' }]) &&
    conversation.participantIds.length === 0
  ) {
    return true
  }

  if (
    access.hasFeatures([{ featureName: 'convo_view_only_mine', accessLevel: 'READ' }]) &&
    conversation.participantIds.includes(currentUserId)
  ) {
    return true
  }

  return (
    conversation.workspaceIds?.some((workspaceId: string) =>
      access.hasPermissionForWorkspace('convo_view_all', 'READ', workspaceId)
    ) ?? true
  )
}

const dispatchNewOrUpdatedConversation = (conversation: ConversationType): void => {
  if (!hasPermissionToSeeConversation(conversation) && receiveNoLongerParticipant(conversation)) {
    store.dispatch(receiveConversationUpdateFromWebsocket(conversation))
  }

  if (!hasPermissionToSeeConversation(conversation) && !isCurrentConversationId(conversation.id)) {
    return
  }

  if (conversation.status === ConversationStatus.BULK_SENT) {
    return
  }

  // DO NOT USE THIS id FOR ANYTHING OTHER THAN A WEBSOCKET CONVERSATION
  if (conversation.endUserIdStr) {
    conversation.endUserId = conversation.endUserIdStr
  }

  if (isConversationInSomeSection(conversation) || isCurrentConversationId(conversation.id)) {
    let actions: Action[] = []
    const storedConversation = getConversation(conversation.id)(store.getState())
    if (
      !storedConversation ||
      (storedConversation && storedConversation.updatedAt <= conversation.updatedAt)
    ) {
      actions.push(receiveConversationUpdateFromWebsocket(conversation))
    }

    if (isConversationInCurrentInbox(conversation)) {
      const countActions = updateCounts(conversation)
      actions = actions.concat(countActions)
    }
    const badgeAction = updateBadgeCounts(conversation)
    if (badgeAction) {
      actions.push(badgeAction)
    }

    batch(() => {
      actions.forEach((action) => store.dispatch(action))
    })
  }
}

export const useGlobalWebsocketHandlers = ({ orgId, currentUserId, currentUserType }: Props) => {
  const dispatch = useDispatch()
  const socketSetupComplete = useSelector<RootState, boolean>(getSocketSetupComplete)
  const orgChannel = useSelector(getOrgChannel)
  return useEffect(() => {
    if (socketSetupComplete && orgId && currentUserId && currentUserType) {
      const ws = getDriftWebSocket()

      ws.on<ConversationType>({
        cluster: 'chat',
        channelName: `${orgChannel}`,
        messageName: 'conversation-new',
      }).subscribe(dispatchNewOrUpdatedConversation)

      ws.on<ConversationType>({
        cluster: 'chat',
        channelName: `${orgChannel}`,
        messageName: 'conversation-updated',
      }).subscribe(dispatchNewOrUpdatedConversation)

      interface SubscriptionEvent {
        eventType: string
        conversation: ConversationType
      }
      ws.on<SubscriptionEvent>({
        cluster: 'chat',
        channelName: `${orgChannel}`,
        messageName: 'subscription-event',
      }).subscribe(({ conversation }) => dispatchNewOrUpdatedConversation(conversation))

      ws.on<DesktopPushNotificationPayload>({
        cluster: 'chat',
        channelName: `user:${orgId}-${currentUserId}-${getUserIdTypeNumber(currentUserType)}`,
        messageName: 'generic-notification',
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        useTrafficControlChannel: true,
      }).subscribe((notification) => {
        const conversationId = notification.body.message.conversationId
        if (conversationId === '') return
        dispatch(
          track({
            name: 'Notification Sent',
            properties: {
              createTime:
                notification &&
                (notification.body?.message?.tracking?.createTime || notification?.timestamp),
              clientCreateTime: new Date().getTime(),
              ws_sentTime: notification && notification.body?.message?.tracking?.sentTime,
              messageId: notification.body.message.messageId,
              conversationId,
              sectionNotification: !notification.body?.message?.tracking,
            },
          })
        )
        dispatch(receiveNotification(notification))
      })

      ws.on<User>({
        cluster: 'chat',
        channelName: `${orgChannel}`,
        messageName: 'user-updated',
      }).subscribe((user) => {
        dispatch(receiveUser(user))
        // Update traffic control redux store
        if (user.id === currentUserId) {
          dispatch(trafficControlUpdateUserConfig(user))
        }
      })

      ws.on<Gates>({
        cluster: CLUSTER.CHAT,
        channelName: `${orgChannel}`,
        messageName: 'gates-updated',
      }).subscribe((newGates) => dispatch(receiveGatesUpdate([currentUserId, newGates])))
    }
  }, [socketSetupComplete, orgId, dispatch, currentUserId, currentUserType, orgChannel])
}
